import { ChatBubbleLeftRightIcon, Cog6ToothIcon, LockClosedIcon, PlusIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useSession } from "../../session/sessionContext";
import { sessionDecision } from "../../session/sessionMachine";
import { appPaths } from "../../shared/routing/appPaths";
import TopbarControl from "../../ui/buttons/TopbarControl";
import DropdownChevron from "../../ui/overlays/DropdownChevron";
import MenuButton, { type MenuSection } from "../../ui/overlays/MenuButton";
import NewProjectDialog from "../create/NewProjectDialog";
import { useProjects } from "../projectContext";

/**
 * The project menu in the top bar (web-app.md WA-5), as Linear's and Vercel's: the current
 * project with its settings, the other projects with a gear each, then "All projects…" and
 * "New project…". No flyout submenus. The DMW core shows, with a lock, to administrators
 * only (F62).
 */
export default function ProjectMenu({ className }: { className?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session } = useSession();
  const { projects, selectedProjectId, selectProject, mayChoose } = useProjects();
  const [creating, setCreating] = useState(false);

  if (projects.status !== "loaded") {
    return null;
  }
  const current = projects.value.find((project) => project.projectId === selectedProjectId) ?? null;
  const others = projects.value.filter((project) => project.projectId !== selectedProjectId && mayChoose(project));
  const settingsOf = (projectId: string) => () => { void navigate(appPaths.projectSettings(projectId)); };
  const gear = <Cog6ToothIcon className="h-4 w-4" />;

  const sections: MenuSection[] = [
    {
      id: "current",
      heading: current?.name,
      headingIsTitle: true,
      items:
        current === null
          ? []
          : [
              { id: "current-settings", label: t("projects.menu.settings"), icon: gear, action: { kind: "select", onSelect: settingsOf(current.projectId) } },
              { id: "current-issues", label: t("projects.menu.issues"), icon: <ChatBubbleLeftRightIcon className="h-4 w-4" />, action: { kind: "select", onSelect: () => { void navigate(appPaths.projectIssues()); } } },
            ],
    },
    {
      id: "others",
      heading: t(current === null ? "projects.menu.choose" : "projects.menu.switch"),
      items: others.map((project) => ({
        id: `project-${project.projectId}`,
        label: project.name,
        icon: project.installationOwned ? <LockClosedIcon className="h-4 w-4" aria-label={t("projects.basis.core")} /> : undefined,
        action: { kind: "select", onSelect: () => { selectProject(project.projectId); } },
        rowAction: {
          id: `settings-${project.projectId}`,
          label: t("projects.menu.settingsOf", { project: project.name }),
          icon: gear,
          onSelect: settingsOf(project.projectId),
        },
      })),
    },
    {
      id: "all",
      items: [
        { id: "all-projects", label: t("projects.menu.allProjects"), icon: <Squares2X2Icon className="h-4 w-4" />, action: { kind: "select", onSelect: () => { void navigate(appPaths.projects()); } } },
        // Refused, it stays in view with why: installation refusals appear where the action is (R3-5).
        { id: "new-project", label: t("projects.menu.newProject"), icon: <PlusIcon className="h-4 w-4" />, requires: sessionDecision(session, "create_project"), action: { kind: "select", onSelect: () => { setCreating(true); } } },
      ],
    },
  ];

  return (
    <>
      <MenuButton
        className={className}
        width="md"
        sections={sections}
        renderTrigger={(trigger, open) => (
          <TopbarControl {...trigger} className="max-w-[14rem] gap-1 px-2" title={t("projects.menu.label")}>
            <span className="sr-only">{t("projects.menu.label")}: </span>
            <span className="truncate">{current?.name ?? t("projects.picker.choose")}</span>
            <DropdownChevron open={open} />
          </TopbarControl>
        )}
      />
      {creating ? <NewProjectDialog onClose={() => { setCreating(false); }} /> : null}
    </>
  );
}
